import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { AppDataService, ConfigService } from 'app/services';
import { MenuHomeEnum } from 'app/components/home';
import { MenuPermissionConfig, MenuPermissionActionEnum } from 'app/models';
import { AppComponent } from 'app/app.component';

export enum PartnerMartLocationsMenuLevel1Enum {
    Stores = 1,
    Groups = 2
}

export enum PartnerMartLocationsMenuLevel2Enum {
    None = 0,

    // Stores
    StoreDetails = 1,
    Inventory = 2,
    Employee = 3,
    StoreHours = 4,
    Handoff = 5,
    LegalDocuments = 6,
    ActiveServices = 7,

    // Groups
    GroupDetails = 8,
    GroupStores = 9,
    GroupInventory = 10,
    GroupStoreHours = 11
}

export enum PartnerMartLocationsMenuLevel3Enum {
    None = 0,

    //Legal Documents
    BusinessPermit = 1,
    BIR2303 = 2
}

@Component({
    selector: 'partner-mart-locations-root',
    templateUrl: './partner-mart-locations-root.component.html',
    styleUrls: ['./partner-mart-locations-root.component.scss',
        '../../../../../../styles/onboardingslidermenu.scss'
    ]
})
export class PartnerMartLocationsRootComponent implements OnInit, OnDestroy {
    public menuEnum = MenuHomeEnum;
    public activeMenu: MenuHomeEnum = MenuHomeEnum.PartnerDashboard;

    public menuPermissionConfig = MenuPermissionConfig;
    public menuPermissionActionEnum = MenuPermissionActionEnum;

    public menuPermissionActive!: string;

    public menuLevel1Enum = PartnerMartLocationsMenuLevel1Enum;
    public activeMenuLevel1Item: PartnerMartLocationsMenuLevel1Enum = PartnerMartLocationsMenuLevel1Enum.Stores;

    public menuLevel2Enum = PartnerMartLocationsMenuLevel2Enum;
    public activeMenuLevel2Item: PartnerMartLocationsMenuLevel2Enum = PartnerMartLocationsMenuLevel2Enum.None;

    public menuLevel3Enum = PartnerMartLocationsMenuLevel3Enum;
    public activeMenuLevel3Item: PartnerMartLocationsMenuLevel3Enum = PartnerMartLocationsMenuLevel3Enum.None;

    // Stores
    public selectedStoreId: number = 0;
    public selectedStore: any = {};
    public showStoreDetails: boolean = false;
    public showInventory: boolean = false;
    public showEmployee: boolean = false;
    public showStoreHours: boolean = false;
    public showHandoff: boolean = false;
    public showLegalDocuments: boolean = false;
    public showActiveServices: boolean = false;

    // Groups
    public selectedGroup: any = {};
    public showGroupDetails: boolean = false;
    public showGroupStores: boolean = false;
    public showGroupInventory: boolean = false;
    public showGroupStoreHours: boolean = false;

    //Legal Documents
    public showBusinessPermit: boolean = false;
    public showBIR2303: boolean = false;

    public merchantId: number = 0;

    private merchanUserSubscription: Subscription | undefined = undefined;

    constructor(private appData: AppDataService, public configService: ConfigService,
        public app: AppComponent) {
        this.onSelectStore = this.onSelectStore.bind(this);
        this.onSelectGroup = this.onSelectGroup.bind(this);
    }

    ngOnInit() {
        this.merchanUserSubscription = this.appData.merchanUserSource$.subscribe((merchanUser: any) => {
            if (!merchanUser) {
                return;
            }

            this.merchantId = merchanUser.MerchantId;
        });
    }

    ngOnDestroy() {
        if (this.merchanUserSubscription) {
            this.merchanUserSubscription.unsubscribe();
        }
    }

    public onMenuLevel1Click(menuItem: PartnerMartLocationsMenuLevel1Enum) {
        this.activeMenuLevel1Item = menuItem;

        // Clear level2 menu
        this.activeMenuLevel2Item = PartnerMartLocationsMenuLevel2Enum.None;

        // Stores
        this.selectedStoreId = 0;
        this.showStoreDetails = false;
        this.showInventory = false;
        this.showEmployee = false;
        this.showStoreHours = false;
        this.showHandoff = false;
        this.showLegalDocuments = false;
        this.showActiveServices = false;

        // Groups
        this.selectedGroup = {};
        this.showGroupDetails = false;
        this.showGroupStores = false;
        this.showGroupInventory = false;
        this.showGroupStoreHours = false;

        //Legal Documents
        this.showBusinessPermit = false;
        this.showBIR2303 = false;
    }

    public onMenuLevel2Click(menuItem: PartnerMartLocationsMenuLevel2Enum) {
        this.activeMenuLevel2Item = menuItem;

        this.updateMenuLevel2();

        if (menuItem === PartnerMartLocationsMenuLevel2Enum.LegalDocuments) {
            this.onMenuLevel3Click(PartnerMartLocationsMenuLevel3Enum.BusinessPermit);
        }
    }

    public onMenuLevel3Click(menuItem: PartnerMartLocationsMenuLevel3Enum) {
        this.activeMenuLevel3Item = menuItem;

        this.updateMenuLevel3();
    }

    public onSelectStore(store: any): void {
        this.selectedStoreId = store.Id;
        this.selectedStore = store;
        this.merchantId = store.MerchantId;
        this.activeMenuLevel2Item = PartnerMartLocationsMenuLevel2Enum.StoreDetails;

        this.updateMenuLevel2();
    }

    public onSelectGroup(group: any): void {
        this.selectedGroup = group;

        this.activeMenuLevel2Item = PartnerMartLocationsMenuLevel2Enum.GroupDetails;

        this.updateMenuLevel2();
    }

    private updateMenuLevel2(): void {
        // Stores
        this.showStoreDetails = this.activeMenuLevel2Item === PartnerMartLocationsMenuLevel2Enum.StoreDetails;
        this.showInventory = this.activeMenuLevel2Item === PartnerMartLocationsMenuLevel2Enum.Inventory;
        this.showEmployee = this.activeMenuLevel2Item === PartnerMartLocationsMenuLevel2Enum.Employee;
        this.showStoreHours = this.activeMenuLevel2Item === PartnerMartLocationsMenuLevel2Enum.StoreHours;
        this.showHandoff = this.activeMenuLevel2Item === PartnerMartLocationsMenuLevel2Enum.Handoff;
        this.showLegalDocuments = this.activeMenuLevel2Item === PartnerMartLocationsMenuLevel2Enum.LegalDocuments;
        this.showActiveServices = this.activeMenuLevel2Item === PartnerMartLocationsMenuLevel2Enum.ActiveServices;

        // Groups
        this.showGroupDetails = this.activeMenuLevel2Item === PartnerMartLocationsMenuLevel2Enum.GroupDetails;
        this.showGroupStores = this.activeMenuLevel2Item === PartnerMartLocationsMenuLevel2Enum.GroupStores;
        this.showGroupInventory = this.activeMenuLevel2Item === PartnerMartLocationsMenuLevel2Enum.GroupInventory;
        this.showGroupStoreHours = this.activeMenuLevel2Item === PartnerMartLocationsMenuLevel2Enum.GroupStoreHours;
    }

    private updateMenuLevel3(): void {
        //Legal Documents
        this.showBusinessPermit = this.activeMenuLevel3Item === PartnerMartLocationsMenuLevel3Enum.BusinessPermit;
        this.showBIR2303 = this.activeMenuLevel3Item === PartnerMartLocationsMenuLevel3Enum.BIR2303;
    }

    public clickMenu(menuPermissionActive: string, menu: MenuHomeEnum) {
        this.menuPermissionActive = menuPermissionActive;
        this.activeMenu = menu;
    }
}
